import React, { useCallback, useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronDownIcon, LoaderIcon, ExternalLinkIcon, PhoneIcon, MapPinIcon, AlertCircleIcon, StoreIcon, SearchIcon, XIcon, MailIcon, SendIcon, CheckCircleIcon, UserIcon, MaximizeIcon, MinimizeIcon, GlobeIcon, ListIcon } from 'lucide-react';
// ─── CONFIGURATION ───────────────────────────────────────────────
const BUSINESS_SHEET_URL = 'https://script.google.com/macros/s/AKfycbzfbBlQDey4gNW6eq5De52ALsOmnzVRl30KjEAzfy4VaaULiQjg0nSIsiS79rJuBqeaxA/exec';
const BUSINESS_SUBMIT_URL = 'https://script.google.com/macros/s/YOUR_BUSINESS_SUBMIT_SCRIPT_ID/exec';
const BUSINESS_DIRECTORY_SHEET_URL = 'https://script.google.com/macros/s/AKfycbyPN8ecbl6WKKLkkZOLcnjE_OC42oHIRqANVt_VqR2jbrPQLzI3aPwXJ0MJJ2Z59rWa/exec';
const STATE_MAP: Record<string, string> = {
  al: 'alabama',
  ak: 'alaska',
  az: 'arizona',
  ar: 'arkansas',
  ca: 'california',
  co: 'colorado',
  ct: 'connecticut',
  de: 'delaware',
  fl: 'florida',
  ga: 'georgia',
  hi: 'hawaii',
  id: 'idaho',
  il: 'illinois',
  in: 'indiana',
  ia: 'iowa',
  ks: 'kansas',
  ky: 'kentucky',
  la: 'louisiana',
  me: 'maine',
  md: 'maryland',
  ma: 'massachusetts',
  mi: 'michigan',
  mn: 'minnesota',
  ms: 'mississippi',
  mo: 'missouri',
  mt: 'montana',
  ne: 'nebraska',
  nv: 'nevada',
  nh: 'new hampshire',
  nj: 'new jersey',
  nm: 'new mexico',
  ny: 'new york',
  nc: 'north carolina',
  nd: 'north dakota',
  oh: 'ohio',
  ok: 'oklahoma',
  or: 'oregon',
  pa: 'pennsylvania',
  ri: 'rhode island',
  sc: 'south carolina',
  sd: 'south dakota',
  tn: 'tennessee',
  tx: 'texas',
  ut: 'utah',
  vt: 'vermont',
  va: 'virginia',
  wa: 'washington',
  wv: 'west virginia',
  wi: 'wisconsin',
  wy: 'wyoming',
  dc: 'district of columbia'
};
const REVERSE_STATE_MAP = Object.entries(STATE_MAP).reduce((acc, [abbr, name]) => {
  acc[name] = abbr;
  return acc;
}, {} as Record<string, string>);
interface Business {
  name: string;
  address?: string;
  city: string;
  state: string;
  lat: number;
  lng: number;
  website?: string;
  phone?: string;
  email?: string;
  description?: string;
  color?: string;
}
interface DirectoryBusiness {
  name: string;
  description?: string;
  city: string;
  state: string;
  zipCode?: string;
  phone?: string;
  website?: string;
}
// ─── COLORED PIN ICONS ──────────────────────────────────────────
const createPinIcon = (fill: string, stroke: string) => L.divIcon({
  className: '',
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40" style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35));">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${fill}" stroke="${stroke}" stroke-width="1.5"/>
      <circle cx="14" cy="14" r="6" fill="white" opacity="0.9"/>
    </svg>`,
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -36]
});
const createStarIcon = (fill: string, stroke: string) => L.divIcon({
  className: '',
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="filter: drop-shadow(0 2px 3px rgba(0,0,0,0.35));"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  iconSize: [34, 34],
  iconAnchor: [17, 34],
  popupAnchor: [0, -30]
});
// Pre-built icon map — saturated palette matching consulate map style
const PIN_ICONS: Record<string, L.DivIcon> = {
  blue: createPinIcon('#2A81CB', '#1E5A8E'),
  green: createPinIcon('#2AAD27', '#1E7B1E'),
  red: createPinIcon('#CB2B3E', '#8E1E2B'),
  pink: createPinIcon('#D252A8', '#8E1E6E'),
  orange: createPinIcon('#CB8427', '#8E5C1E'),
  purple: createPinIcon('#9C2BCB', '#6E1E8E'),
  yellow: createPinIcon('#CAC428', '#8E891E'),
  teal: createPinIcon('#2AADAD', '#1E7B7B'),
  navy: createPinIcon('#003F87', '#001A3E'),
  'gold star': createStarIcon('#D4A017', '#9E7612'),
  'silver star': createStarIcon('#7B8A98', '#525E6A'),
  'red star': createStarIcon('#CB2B3E', '#8E1E2B'),
  'blue star': createStarIcon('#2A81CB', '#1E5A8E'),
  'green star': createStarIcon('#2AAD27', '#1E7B1E')
};
// Default blue pin fallback (matches consulate map marker blue)
const defaultIcon = createPinIcon('#2A81CB', '#1E5A8E');
// Get the right icon based on the color column value
const getBusinessIcon = (biz: Business): L.DivIcon => {
  const colorVal = (biz.color || '').trim().toLowerCase();
  // Direct match (e.g. "gold star", "blue", "green")
  if (PIN_ICONS[colorVal]) return PIN_ICONS[colorVal];
  // Match "X pin" format (e.g. "green pin" → "green")
  const pinMatch = colorVal.match(/^(\w+)\s+pin$/);
  if (pinMatch && PIN_ICONS[pinMatch[1]]) return PIN_ICONS[pinMatch[1]];
  // Match "X star" format
  const starMatch = colorVal.match(/^(\w+)\s+star$/);
  if (starMatch) {
    const key = `${starMatch[1]} star`;
    if (PIN_ICONS[key]) return PIN_ICONS[key];
    // Fallback: gold star for any unrecognized star
    return PIN_ICONS['gold star'];
  }
  // Legacy: Distinguished Business check
  if (biz.description && biz.description.trim().toLowerCase().startsWith('distinguished business-')) {
    return PIN_ICONS['gold star'];
  }
  return defaultIcon;
};
// Helper to check if a business is Distinguished (description starts with "Distinguished Business-")
const isVipBusiness = (biz: Business): boolean => !!(biz.description && biz.description.trim().toLowerCase().startsWith('distinguished business-'));
// Helper to get the clean description (without "Distinguished Business-" prefix)
const getCleanDescription = (description: string): string => description.trim().replace(/^distinguished business-\s*/i, '').trim();
// Helper component to fly to the first searched city
function FlyToCity({
  businesses,
  searchQuery
}: {
  businesses: Business[];
  searchQuery: string;
}) {
  const map = useMap();
  useEffect(() => {
    if (searchQuery.trim() && businesses.length > 0) {
      const firstMatch = businesses[0];
      if (firstMatch.lat && firstMatch.lng) {
        map.flyTo([firstMatch.lat, firstMatch.lng], 11, {
          duration: 1.5
        });
      }
    } else if (!searchQuery.trim()) {
      // Reset to USA view when search is cleared
      map.flyTo([39.8283, -98.5795], 4, {
        duration: 1.5
      });
    }
  }, [searchQuery, businesses, map]);
  return null;
}
function MapResizer({
  isExpanded
}: {
  isExpanded: boolean;
}) {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);
    return () => clearTimeout(timer);
  }, [isExpanded, map]);
  return null;
}
export function BusinessMaps() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [usaOpen, setUsaOpen] = useState(true);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [onlineBusinesses, setOnlineBusinesses] = useState<Business[]>([]);
  const [onlineOpen, setOnlineOpen] = useState(false);
  const [expandedOnlineBiz, setExpandedOnlineBiz] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [bizName, setBizName] = useState('');
  const [bizType, setBizType] = useState('');
  const [bizAddress, setBizAddress] = useState('');
  const [bizCity, setBizCity] = useState('');
  const [bizState, setBizState] = useState('');
  const [bizZip, setBizZip] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizWebsite, setBizWebsite] = useState('');
  const [bizContact, setBizContact] = useState('');
  const [bizSubmitted, setBizSubmitted] = useState(false);
  const [bizSubmitting, setBizSubmitting] = useState(false);
  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [directoryBusinesses, setDirectoryBusinesses] = useState<DirectoryBusiness[]>([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [directoryHasFetched, setDirectoryHasFetched] = useState(false);
  const [directorySearchQuery, setDirectorySearchQuery] = useState('');
  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const resetBizForm = () => {
    setBizName('');
    setBizType('');
    setBizAddress('');
    setBizCity('');
    setBizState('');
    setBizZip('');
    setBizPhone('');
    setBizEmail('');
    setBizWebsite('');
    setBizContact('');
    setBizSubmitted(false);
    setBizSubmitting(false);
  };
  const handleBizSubmit = () => {
    if (!bizFormValid || bizSubmitting) return;
    setBizSubmitting(true);
    const data = {
      businessName: bizName,
      businessType: bizType,
      address: bizAddress,
      city: bizCity,
      state: bizState,
      zipCode: bizZip,
      phone: bizPhone,
      email: bizEmail,
      website: bizWebsite,
      contactPerson: bizContact,
      timestamp: new Date().toISOString()
    };
    fetch(BUSINESS_SUBMIT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(data)
    }).catch(err => {
      console.warn('Business submission error:', err);
    });
    setBizSubmitting(false);
    setBizSubmitted(true);
  };
  const bizFormValid = bizName.trim() && bizCity.trim() && bizState.trim() && bizContact.trim();
  const fetchBusinesses = useCallback(async (force = false) => {
    if (hasFetched && !force) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(BUSINESS_SHEET_URL, {
        redirect: 'follow'
      });
      const text = await response.text();
      console.log('[BusinessMaps] Raw response (first 500 chars):', text.substring(0, 500));
      // Apps Script sometimes returns with extra whitespace or BOM
      const cleanText = text.trim().replace(/^\uFEFF/, '');
      let json: any;
      try {
        json = JSON.parse(cleanText);
      } catch (parseErr) {
        console.error('[BusinessMaps] JSON parse failed. Response was:', cleanText.substring(0, 200));
        throw new Error('Invalid JSON response from Apps Script');
      }
      const rows = json.businesses || json.data || (Array.isArray(json) ? json : []);
      const mapped: Business[] = [];
      const online: Business[] = [];
      const ONLINE_KEYWORDS = ['online', 'nationwide', 'national', 'en línea', 'nacional'];
      // Log first row keys to debug column names
      if (rows.length > 0) {
        console.log('[BusinessMaps] Row keys:', Object.keys(rows[0]));
        console.log('[BusinessMaps] First row sample:', JSON.stringify(rows[0]).substring(0, 300));
      }
      rows.forEach((row: any) => {
        if (!row.name) return;
        const lat = row.lat || row.latitude;
        const lng = row.lng || row.ing || row.longitude;
        const hasCoords = lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng));
        const stateVal = String(row.state || row.State || '').trim();
        const isOnline = ONLINE_KEYWORDS.some(kw => stateVal.toLowerCase().includes(kw));
        if (isOnline) {
          console.log('[BusinessMaps] Online business detected:', row.name, '| state:', stateVal);
        }
        const biz: Business = {
          name: String(row.name || '').trim(),
          address: String(row.address || '').trim() || undefined,
          city: String(row.city || '').trim(),
          state: stateVal,
          lat: hasCoords ? Number(lat) : 0,
          lng: hasCoords ? Number(lng) : 0,
          website: String(row.website || row.url || '').trim() || undefined,
          phone: String(row.phone || '').trim() || undefined,
          email: String(row.email || '').trim() || undefined,
          description: String(row.description || '').trim() || undefined,
          color: String(row.color || row.Color || '').trim() || undefined
        };
        if (isOnline || !hasCoords) {
          online.push(biz);
        } else {
          mapped.push(biz);
        }
      });
      console.log('[BusinessMaps] Loaded', mapped.length, 'map businesses and', online.length, 'online businesses from', rows.length, 'rows');
      setBusinesses(mapped);
      setOnlineBusinesses(online);
      setHasFetched(true);
    } catch (err) {
      console.error('[BusinessMaps] Fetch error:', err);
      setError(isEs ? 'No se pudieron cargar los datos de empresas.' : 'Could not load business data.');
    } finally {
      setLoading(false);
    }
  }, [hasFetched, isEs]);
  const fetchDirectoryBusinesses = useCallback(async (force = false) => {
    if (directoryHasFetched && !force) return;
    setDirectoryLoading(true);
    setDirectoryError(null);
    try {
      const response = await fetch(BUSINESS_DIRECTORY_SHEET_URL, {
        redirect: 'follow'
      });
      const text = await response.text();
      const cleanText = text.trim().replace(/^\uFEFF/, '');
      let json: any;
      try {
        json = JSON.parse(cleanText);
      } catch (parseErr) {
        throw new Error('Invalid JSON response from Apps Script');
      }
      const rows = json.businesses || json.data || (Array.isArray(json) ? json : []);
      const mapped: DirectoryBusiness[] = [];
      rows.forEach((row: any) => {
        if (!row.name && !row.Name) return;
        mapped.push({
          name: String(row.name || row.Name || '').trim(),
          description: String(row.description || row.Description || '').trim() || undefined,
          city: String(row.city || row.City || '').trim(),
          state: String(row.state || row.State || '').trim(),
          zipCode: String(row.zipCode || row.zip || row['Zip Code'] || '').trim() || undefined,
          phone: String(row.phone || row.Phone || '').trim() || undefined,
          website: String(row.website || row.url || row.Website || '').trim() || undefined
        });
      });
      setDirectoryBusinesses(mapped);
      setDirectoryHasFetched(true);
    } catch (err) {
      console.error('[BusinessMaps] Directory fetch error:', err);
      setDirectoryError(isEs ? 'No se pudieron cargar los datos del directorio.' : 'Could not load directory data.');
    } finally {
      setDirectoryLoading(false);
    }
  }, [directoryHasFetched, isEs]);
  // Fetch when the accordion opens for the first time
  useEffect(() => {
    if (usaOpen && !hasFetched) {
      fetchBusinesses();
    }
  }, [usaOpen, hasFetched, fetchBusinesses]);
  useEffect(() => {
    if (directoryOpen && !directoryHasFetched) {
      fetchDirectoryBusinesses();
    }
  }, [directoryOpen, directoryHasFetched, fetchDirectoryBusinesses]);
  // Filter businesses by name, city, or state search
  const filteredBusinesses = businesses.filter(biz => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const stateAbbr = REVERSE_STATE_MAP[q] || (STATE_MAP[q] ? q : null);
    const stateName = STATE_MAP[q] || (REVERSE_STATE_MAP[q] ? q : null);
    const bizStateLower = biz.state.toLowerCase();
    return biz.name.toLowerCase().includes(q) || biz.city.toLowerCase().includes(q) || bizStateLower.includes(q) || stateAbbr && bizStateLower === stateAbbr || stateName && bizStateLower === stateName;
  });
  const filteredOnlineBusinesses = onlineBusinesses.filter(biz => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    return biz.name.toLowerCase().includes(q) || biz.city.toLowerCase().includes(q) || biz.state.toLowerCase().includes(q) || biz.description && biz.description.toLowerCase().includes(q);
  });
  const filteredDirectoryBusinesses = directoryBusinesses.filter(biz => {
    if (!directorySearchQuery.trim()) return true;
    const q = directorySearchQuery.toLowerCase().trim();
    return biz.name.toLowerCase().includes(q) || biz.city.toLowerCase().includes(q) || biz.state.toLowerCase().includes(q) || biz.description && biz.description.toLowerCase().includes(q);
  });
  return <>
      <div className="mt-16" data-id="element-229">
        <div className="text-center mb-12" data-id="element-230">
          <div className="flex items-center justify-center gap-3 mt-6" data-id="element-231">
            <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-232" />
            <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-233" />
            <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-234" />
          </div>
          <a href="#/business-map" className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-300" data-id="element-235">
            <ExternalLinkIcon className="w-4 h-4" data-id="element-236" />
            {isEs ? 'Abrir Mapa en Página Completa' : 'Open Full Map Page'}
          </a>
        </div>

        <div className="mb-12" data-id="element-237">
          {/* USA Map */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-t-4 border-haa-navy flex flex-col group hover:shadow-xl transition-shadow duration-300" data-id="element-238">
            <button onClick={() => setUsaOpen(prev => !prev)} className="w-full p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-navy" aria-expanded={usaOpen} data-id="element-239">
              <div data-id="element-240">
                <div className="flex items-center gap-3" data-id="element-241">
                  <h4 className="font-bebas text-2xl text-haa-navy tracking-wide m-0" data-id="element-242">
                    {isEs ? 'Empresas Hondureño-Americanas — Estados Unidos' : 'Honduran-American Businesses — United States'}
                  </h4>
                  {hasFetched && businesses.length > 0 && <span className="inline-flex items-center gap-1 bg-haa-navy/10 text-haa-navy text-xs font-nunito font-bold px-2.5 py-1 rounded-full" data-id="element-243">
                      <StoreIcon className="w-3 h-3" data-id="element-244" />
                      {businesses.length}
                    </span>}
                </div>
                <p className="font-nunito text-sm text-gray-500 mt-1" data-id="element-245">
                  {isEs ? 'Mapa de empresas hondureño-americanas en todo Estados Unidos. (Haz clic en la flecha para expandir el mapa)' : 'Map of Honduran-American owned businesses across the USA. (Click on Arrow to Expand Map)'}
                </p>
                <p className="font-nunito text-[11px] text-gray-400 mt-1.5 leading-snug italic" data-id="element-246">
                  {isEs ? 'Aviso: Este listado es solo para fines informativos y no constituye un respaldo. Ninguna empresa ha pagado para ser incluida. HAA investiga y evalúa las empresas de forma independiente. Los consumidores deben realizar su propia investigación antes de contratar cualquier servicio.' : 'Disclaimer: This listing is for informational purposes only and does not constitute an endorsement. No business has paid in any way to be listed. HAA continuously researches and vets businesses independently. Consumers should do their own vetting and research prior to engaging.'}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 text-haa-navy flex-shrink-0 transition-transform duration-300 ${usaOpen ? 'rotate-180' : ''}`} data-id="element-247" />
            </button>

            {usaOpen && <div className={isMapExpanded ? 'fixed inset-0 z-[100] bg-white flex flex-col' : 'relative flex flex-col'} data-id="element-248">
                {isMapExpanded && <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white z-20" data-id="element-249">
                    <h3 className="font-bebas text-2xl text-haa-navy tracking-wider m-0" data-id="element-250">
                      {isEs ? 'Mapa de Empresas' : 'Business Map'}
                    </h3>
                    <button onClick={() => setIsMapExpanded(false)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500" data-id="element-251">
                      <XIcon className="w-6 h-6" data-id="element-252" />
                    </button>
                  </div>}
                {/* Search Bar */}
                <div className="sticky top-0 z-20 bg-white border-b border-gray-100 p-4 shadow-sm" data-id="element-253">
                  <div className="relative max-w-md mx-auto" data-id="element-254">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-255">
                      <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-256" />
                    </div>
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={isEs ? 'Buscar por nombre, ciudad o estado...' : 'Search by name, city, or state...'} className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue sm:text-sm font-nunito transition-colors" data-id="element-257" />
                    {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Clear search" data-id="element-258">
                        <XIcon className="h-5 w-5" data-id="element-259" />
                      </button>}
                  </div>
                  {searchQuery && <div className="text-center mt-2" data-id="element-260">
                      <span className="font-nunito text-xs font-semibold text-haa-blue bg-blue-50 px-3 py-1 rounded-full" data-id="element-261">
                        {isEs ? `Mostrando ${filteredBusinesses.length} de ${businesses.length} empresas` : `Showing ${filteredBusinesses.length} of ${businesses.length} businesses`}
                      </span>
                    </div>}
                </div>

                <div className={isMapExpanded ? 'flex-1 relative' : 'relative'} data-id="element-262">
                  {/* Loading overlay */}
                  {loading && <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3" data-id="element-263">
                      <LoaderIcon className="w-8 h-8 text-haa-navy animate-spin" data-id="element-264" />
                      <p className="font-nunito text-sm text-haa-navy font-semibold" data-id="element-265">
                        {isEs ? 'Cargando empresas...' : 'Loading businesses...'}
                      </p>
                    </div>}

                  {/* Error message */}
                  {error && !loading && <div className="absolute inset-0 z-10 bg-white/90 flex flex-col items-center justify-center gap-3 p-6" data-id="element-266">
                      <AlertCircleIcon className="w-8 h-8 text-red-500" data-id="element-267" />
                      <p className="font-nunito text-sm text-red-600 text-center" data-id="element-268">
                        {error}
                      </p>
                      <button onClick={() => {
                  setHasFetched(false);
                  setError(null);
                  fetchBusinesses();
                }} className="font-nunito text-sm text-haa-navy underline hover:opacity-70" data-id="element-269">
                        {isEs ? 'Reintentar' : 'Try again'}
                      </button>
                    </div>}

                  <div className={isMapExpanded ? 'w-full h-full relative z-0' : 'h-[400px] w-full relative z-0'} data-id="element-270">
                    <MapContainer center={[39.8283, -98.5795]} zoom={4} scrollWheelZoom={false} className="w-full h-full" data-id="element-271">
                      <MapResizer isExpanded={isMapExpanded} data-id="element-272" />
                      <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-273" />

                      <FlyToCity businesses={filteredBusinesses} searchQuery={searchQuery} data-id="element-274" />

                      {/* Dynamic markers from Google Sheet via Apps Script */}
                      {filteredBusinesses.map((biz, idx) => <Marker key={`${biz.name}-${idx}`} position={[biz.lat, biz.lng]} icon={getBusinessIcon(biz)} data-id="element-275">
                          <Popup className="font-nunito" maxWidth={280} data-id="element-276">
                            <div className="min-w-[200px]" data-id="element-277">
                              <div className="font-bold text-haa-navy text-base leading-tight mb-2" data-id="element-278">
                                {biz.name}
                              </div>

                              {biz.address && <div className="flex items-start gap-1.5 mt-2 text-sm text-gray-700" data-id="element-279">
                                  <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-280" />
                                  <span className="leading-tight" data-id="element-281">
                                    {biz.address}
                                  </span>
                                </div>}

                              {(biz.city || biz.state) && <div className="flex items-center gap-1.5 mt-1 text-sm text-gray-600 pl-5.5" data-id="element-282">
                                  <span className="ml-[22px]" data-id="element-283">
                                    {[biz.city, biz.state].filter(Boolean).join(', ')}
                                  </span>
                                </div>}

                              {isVipBusiness(biz) && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full mt-2" data-id="element-284">
                                  ⭐ Distinguished Business
                                </span>}

                              {biz.description && <p className="text-xs text-gray-500 mt-3 leading-relaxed bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-285">
                                  {isVipBusiness(biz) ? getCleanDescription(biz.description) : biz.description}
                                </p>}

                              <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-gray-100" data-id="element-286">
                                {biz.phone && <a href={`tel:${biz.phone}`} className="inline-flex items-center gap-2 text-xs text-haa-blue hover:text-haa-navy hover:underline font-semibold transition-colors" data-id="element-287">
                                    <PhoneIcon className="w-3.5 h-3.5" data-id="element-288" />
                                    {biz.phone}
                                  </a>}
                                {biz.email && <a href={`mailto:${biz.email}`} className="inline-flex items-center gap-2 text-xs text-haa-blue hover:text-haa-navy hover:underline font-semibold transition-colors" data-id="element-289">
                                    <MailIcon className="w-3.5 h-3.5" data-id="element-290" />
                                    {biz.email}
                                  </a>}
                                {biz.website && <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-haa-blue hover:text-haa-navy hover:underline font-semibold transition-colors" data-id="element-291">
                                    <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-292" />
                                    {isEs ? 'Sitio Web' : 'Website'}
                                  </a>}
                              </div>
                            </div>
                          </Popup>
                        </Marker>)}

                      {/* Show placeholder if no businesses loaded yet */}
                      {hasFetched && businesses.length === 0 && !error && !searchQuery && <Marker position={[38.9072, -77.0369]} icon={defaultIcon} data-id="element-293">
                            <Popup className="font-nunito" data-id="element-294">
                              <div className="font-bold text-haa-navy text-base" data-id="element-295">
                                Washington D.C.
                              </div>
                              <div className="text-sm text-gray-600 mt-1" data-id="element-296">
                                {isEs ? 'Próximamente listados de empresas.' : 'Business listings coming soon.'}
                              </div>
                            </Popup>
                          </Marker>}
                    </MapContainer>
                    <button onClick={() => setIsMapExpanded(!isMapExpanded)} className="absolute top-4 right-4 z-[400] bg-white p-2 rounded-md shadow-md hover:bg-gray-50 focus:outline-none border border-gray-200" aria-label={isMapExpanded ? 'Minimize map' : 'Expand map'} data-id="element-297">
                      {isMapExpanded ? <MinimizeIcon className="w-5 h-5 text-gray-700" data-id="element-298" /> : <MaximizeIcon className="w-5 h-5 text-gray-700" data-id="element-299" />}
                    </button>
                  </div>
                </div>

                {/* Business count footer */}
                {hasFetched && businesses.length > 0 && <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between" data-id="element-300">
                    <p className="font-nunito text-xs text-gray-500" data-id="element-301">
                      {searchQuery ? isEs ? `${filteredBusinesses.length} resultados encontrados` : `${filteredBusinesses.length} results found` : isEs ? `${businesses.length} empresa${businesses.length !== 1 ? 's' : ''} listada${businesses.length !== 1 ? 's' : ''}` : `${businesses.length} business${businesses.length !== 1 ? 'es' : ''} listed`}
                    </p>
                    <button onClick={() => {
                setHasFetched(false);
                fetchBusinesses(true);
              }} className="font-nunito text-[10px] text-haa-blue hover:text-haa-navy underline font-semibold transition-colors" data-id="element-302">
                      {isEs ? '↻ Actualizar datos' : '↻ Refresh data'}
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </div>

      {/* ── Business Directory Accordion ── */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-t-4 border-haa-midblue mb-12" data-id="element-303">
        <button onClick={() => setDirectoryOpen(prev => !prev)} className="w-full p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-midblue" aria-expanded={directoryOpen} data-id="element-304">
          <div data-id="element-305">
            <div className="flex items-center gap-3" data-id="element-306">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-midblue to-haa-blue flex items-center justify-center shadow-md" data-id="element-307">
                <ListIcon className="w-5 h-5 text-white" data-id="element-308" />
              </div>
              <h4 className="font-bebas text-2xl text-haa-navy tracking-wide m-0" data-id="element-309">
                {isEs ? 'Directorio de Empresas Hondureño-Americanas' : 'Honduran-American Business Directory'}
              </h4>
              {directoryHasFetched && directoryBusinesses.length > 0 && <span className="inline-flex items-center gap-1 bg-haa-midblue/10 text-haa-midblue text-xs font-nunito font-bold px-2.5 py-1 rounded-full" data-id="element-310">
                  <ListIcon className="w-3 h-3" data-id="element-311" />
                  {directoryBusinesses.length}
                </span>}
            </div>
            <p className="font-nunito text-sm text-gray-500 mt-2 ml-[52px]" data-id="element-312">
              {isEs ? 'Explora nuestro directorio completo de empresas en formato de lista.' : 'Explore our complete business directory in list format.'}
            </p>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-haa-navy flex-shrink-0 transition-transform duration-300 ${directoryOpen ? 'rotate-180' : ''}`} data-id="element-313" />
        </button>

        {directoryOpen && <div className="flex flex-col relative" data-id="element-314">
            {/* Search Bar */}
            <div className="sticky top-0 z-20 bg-white border-b border-gray-100 p-4 shadow-sm" data-id="element-315">
              <div className="relative max-w-md mx-auto" data-id="element-316">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-317">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-318" />
                </div>
                <input type="text" value={directorySearchQuery} onChange={e => setDirectorySearchQuery(e.target.value)} placeholder={isEs ? 'Buscar por nombre, ciudad, estado o descripción...' : 'Search by name, city, state, or description...'} className="block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-haa-midblue focus:border-haa-midblue sm:text-sm font-nunito transition-colors" data-id="element-319" />
                {directorySearchQuery && <button onClick={() => setDirectorySearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none" aria-label="Clear search" data-id="element-320">
                    <XIcon className="h-5 w-5" data-id="element-321" />
                  </button>}
              </div>
              {directorySearchQuery && <div className="text-center mt-2" data-id="element-322">
                  <span className="font-nunito text-xs font-semibold text-haa-midblue bg-blue-50 px-3 py-1 rounded-full" data-id="element-323">
                    {isEs ? `Mostrando ${filteredDirectoryBusinesses.length} de ${directoryBusinesses.length} empresas` : `Showing ${filteredDirectoryBusinesses.length} of ${directoryBusinesses.length} businesses`}
                  </span>
                </div>}
            </div>

            <div className="p-6 sm:p-8 bg-gray-50 min-h-[300px] relative" data-id="element-324">
              {/* Loading overlay */}
              {directoryLoading && <div className="absolute inset-0 z-10 bg-gray-50/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3" data-id="element-325">
                  <LoaderIcon className="w-8 h-8 text-haa-navy animate-spin" data-id="element-326" />
                  <p className="font-nunito text-sm text-haa-navy font-semibold" data-id="element-327">
                    {isEs ? 'Cargando directorio...' : 'Loading directory...'}
                  </p>
                </div>}

              {/* Error message */}
              {directoryError && !directoryLoading && <div className="absolute inset-0 z-10 bg-gray-50 flex flex-col items-center justify-center gap-3 p-6" data-id="element-328">
                  <AlertCircleIcon className="w-8 h-8 text-red-500" data-id="element-329" />
                  <p className="font-nunito text-sm text-red-600 text-center" data-id="element-330">
                    {directoryError}
                  </p>
                  <button onClick={() => {
              setDirectoryHasFetched(false);
              fetchDirectoryBusinesses(true);
            }} className="font-nunito text-sm text-haa-navy underline hover:opacity-70" data-id="element-331">
                    {isEs ? 'Reintentar' : 'Try again'}
                  </button>
                </div>}

              {/* Empty state */}
              {directoryHasFetched && filteredDirectoryBusinesses.length === 0 && !directoryError && <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-332">
                    <StoreIcon className="w-12 h-12 text-gray-300 mb-3" data-id="element-333" />
                    <p className="font-nunito text-gray-500" data-id="element-334">
                      {isEs ? 'No se encontraron empresas.' : 'No businesses found.'}
                    </p>
                  </div>}

              {/* Directory Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-335">
                {filteredDirectoryBusinesses.map((biz, idx) => <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow flex flex-col h-full" data-id="element-336">
                    <h5 className="font-bold text-haa-navy text-lg leading-tight mb-2" data-id="element-337">
                      {biz.name}
                    </h5>

                    {biz.description && <p className="font-nunito text-sm text-gray-500 mb-4 line-clamp-3 flex-grow" data-id="element-338">
                        {biz.description}
                      </p>}

                    <div className="mt-auto space-y-2 pt-4 border-t border-gray-50" data-id="element-339">
                      {(biz.city || biz.state || biz.zipCode) && <div className="flex items-start gap-2 text-sm text-gray-600" data-id="element-340">
                          <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" data-id="element-341" />
                          <span className="leading-tight" data-id="element-342">
                            {[biz.city, biz.state, biz.zipCode].filter(Boolean).join(', ')}
                          </span>
                        </div>}

                      {biz.phone && <a href={`tel:${biz.phone}`} className="flex items-center gap-2 text-sm text-haa-blue hover:text-haa-navy transition-colors w-fit" data-id="element-343">
                          <PhoneIcon className="w-4 h-4 flex-shrink-0" data-id="element-344" />
                          <span data-id="element-345">{biz.phone}</span>
                        </a>}

                      {biz.website && <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-haa-blue hover:text-haa-navy transition-colors w-fit" data-id="element-346">
                          <GlobeIcon className="w-4 h-4 flex-shrink-0" data-id="element-347" />
                          <span className="truncate max-w-[200px]" data-id="element-348">
                            {biz.website.replace(/^https?:\/\//, '')}
                          </span>
                        </a>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}
      </div>

      {/* ── Business Submission Form ── */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-t-4 border-haa-skyblue" data-id="element-349">
        <button onClick={() => setFormOpen(prev => !prev)} className="w-full p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-skyblue" aria-expanded={formOpen} data-id="element-350">
          <div data-id="element-351">
            <div className="flex items-center gap-3" data-id="element-352">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-skyblue to-haa-midblue flex items-center justify-center shadow-md" data-id="element-353">
                <StoreIcon className="w-5 h-5 text-white" data-id="element-354" />
              </div>
              <h4 className="font-bebas text-2xl text-haa-navy tracking-wide m-0" data-id="element-355">
                {isEs ? 'Registra Tu Empresa' : 'Register Your Business'}
              </h4>
            </div>
            <p className="font-nunito text-sm text-gray-500 mt-2 ml-[52px]" data-id="element-356">
              {isEs ? '¿Tienes una empresa hondureño-americana? ¡Envíala para aparecer en nuestro mapa!' : 'Own a Honduran-American business? Submit it to be featured on our map!'}
            </p>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-haa-navy flex-shrink-0 transition-transform duration-300 ${formOpen ? 'rotate-180' : ''}`} data-id="element-357" />
        </button>

        {formOpen && <div className="p-6 sm:p-8" data-id="element-358">
            {bizSubmitted ? <div className="text-center py-12" data-id="element-359">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-360">
                  <CheckCircleIcon className="w-8 h-8 text-emerald-600" data-id="element-361" />
                </div>
                <h4 className="font-bebas text-3xl text-haa-navy tracking-wider mb-2" data-id="element-362">
                  {isEs ? '¡Gracias!' : 'Thank You!'}
                </h4>
                <p className="font-nunito text-gray-600 max-w-md mx-auto" data-id="element-363">
                  {isEs ? 'Tu empresa ha sido enviada para revisión. Aparecerá en el mapa una vez aprobada.' : 'Your business has been submitted for review. It will appear on the map once approved.'}
                </p>
                <button onClick={resetBizForm} className="mt-6 font-nunito text-sm font-bold text-haa-blue hover:text-haa-navy underline transition-colors" data-id="element-364">
                  {isEs ? 'Enviar otra empresa' : 'Submit another business'}
                </button>
              </div> : <div className="space-y-5" data-id="element-365">
                <p className="font-nunito text-gray-600 text-sm" data-id="element-366">
                  {isEs ? 'Completa el formulario a continuación para agregar tu empresa al directorio. Los campos marcados con * son obligatorios.' : 'Fill out the form below to add your business to the directory. Fields marked with * are required.'}
                </p>

                {/* Row 1: Business Name + Type */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-367">
                  <div data-id="element-368">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-369">
                      {isEs ? 'Nombre de la Empresa' : 'Business Name'} *
                    </label>
                    <input type="text" value={bizName} onChange={e => setBizName(e.target.value)} placeholder={isEs ? 'Ej: Mi Restaurante' : 'e.g. My Restaurant'} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-370" />
                  </div>
                  <div data-id="element-371">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-372">
                      {isEs ? 'Tipo de Negocio' : 'Type of Business'}
                    </label>
                    <input type="text" value={bizType} onChange={e => setBizType(e.target.value)} placeholder={isEs ? 'Ej: Restaurante, Tienda' : 'e.g. Restaurant, Store'} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-373" />
                  </div>
                </div>

                {/* Row 2: Address */}
                <div data-id="element-374">
                  <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-375">
                    {isEs ? 'Dirección' : 'Address'}
                  </label>
                  <input type="text" value={bizAddress} onChange={e => setBizAddress(e.target.value)} placeholder={isEs ? 'Dirección completa' : 'Full street address'} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-376" />
                </div>

                {/* Row 3: City, State, Zip */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-377">
                  <div data-id="element-378">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-379">
                      {isEs ? 'Ciudad' : 'City'} *
                    </label>
                    <input type="text" value={bizCity} onChange={e => setBizCity(e.target.value)} placeholder={isEs ? 'Ej: Houston' : 'e.g. Houston'} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-380" />
                  </div>
                  <div data-id="element-381">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-382">
                      {isEs ? 'Estado' : 'State'} *
                    </label>
                    <input type="text" value={bizState} onChange={e => setBizState(e.target.value)} placeholder={isEs ? 'Ej: TX' : 'e.g. TX'} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-383" />
                  </div>
                  <div data-id="element-384">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-385">
                      {isEs ? 'Código Postal' : 'Zip Code'}
                    </label>
                    <input type="text" value={bizZip} onChange={e => setBizZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="e.g. 77001" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-386" />
                  </div>
                </div>

                {/* Row 4: Phone + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-387">
                  <div data-id="element-388">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-389">
                      {isEs ? 'Teléfono' : 'Phone'}
                    </label>
                    <input type="tel" value={bizPhone} onChange={e => setBizPhone(formatPhoneNumber(e.target.value))} placeholder="e.g. 713-555-1234" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-390" />
                  </div>
                  <div data-id="element-391">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-392">
                      {isEs ? 'Correo Electrónico' : 'Email'}
                    </label>
                    <input type="email" value={bizEmail} onChange={e => setBizEmail(e.target.value)} placeholder="e.g. info@mybusiness.com" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-393" />
                  </div>
                </div>

                {/* Row 5: Website + Contact Person */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-394">
                  <div data-id="element-395">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-396">
                      {isEs ? 'Sitio Web' : 'Website'}
                    </label>
                    <input type="url" value={bizWebsite} onChange={e => setBizWebsite(e.target.value)} placeholder="e.g. www.mybusiness.com" className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-397" />
                  </div>
                  <div data-id="element-398">
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1" data-id="element-399">
                      {isEs ? 'Persona de Contacto' : 'Contact Person'} *
                    </label>
                    <input type="text" value={bizContact} onChange={e => setBizContact(e.target.value)} placeholder={isEs ? 'Nombre completo' : 'Full name'} className="w-full px-4 py-2.5 border border-gray-300 rounded-xl font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-haa-skyblue focus:border-haa-skyblue transition-colors" data-id="element-400" />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2" data-id="element-401">
                  <button onClick={handleBizSubmit} disabled={!bizFormValid || bizSubmitting} className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-nunito font-bold text-sm transition-all duration-300 shadow-md ${bizFormValid && !bizSubmitting ? 'bg-haa-navy hover:bg-haa-blue text-white hover:shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-402">
                    {bizSubmitting ? <>
                        <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-403" />
                        {isEs ? 'Enviando...' : 'Submitting...'}
                      </> : <>
                        <SendIcon className="w-4 h-4" data-id="element-404" />
                        {isEs ? 'Enviar Empresa' : 'Submit Business'}
                      </>}
                  </button>
                  <p className="font-nunito text-xs text-gray-400 mt-3" data-id="element-405">
                    {isEs ? '* Campos obligatorios. Tu información será revisada antes de publicarse.' : '* Required fields. Your information will be reviewed before being published.'}
                  </p>
                </div>
              </div>}
          </div>}
      </div>
    </>;
}